// CartSection.tsx
import React from "react";
import {
  MdAddShoppingCart,
  MdPlaylistAdd,
  MdDelete,
  MdRemove,
  MdAdd,
  MdCheck,
} from "react-icons/md";
import { useTheme } from "../../hooks/useTheme";
import styles from "./CartSection.module.scss";
import { unitTypeToText } from "../../utils/functions/enumToText";
import { formatPriceToLocale } from "../../utils/functions/formatPriceToLocale";

interface CartSectionProps {
  price?: number;
  unit?: number;
  quantity: number;
  onAdd: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove: () => void;
  layout?: "item" | "group";
  appearance?: "default" | "compact";
  isOutOfStock?: boolean;
  maxAvailable?: number;
  isIncreaseDisabled?: boolean;
  isComplexCategory?: boolean;
  showComplexButton?: boolean;
  showCartButton?: boolean;
  onAddToComplex?: () => void;
  isInComplexCart?: boolean;
}

export const CartSection: React.FC<CartSectionProps> = ({
  price,
  unit,
  quantity,
  onAdd,
  onIncrease,
  onDecrease,
  onRemove,
  layout = "item",
  appearance,
  isOutOfStock,
  isIncreaseDisabled,
  maxAvailable,
  showComplexButton,
  showCartButton = true,
  onAddToComplex,
  isInComplexCart = false,
}) => {
  const { theme } = useTheme();
  const isCompact = appearance ? appearance === "compact" : true;
  const hasPrice = Boolean(price);
  const unitText =
    unit !== undefined && unit !== -1 ? unitTypeToText(unit) : "";

  const renderQuantityControls = () => (
    <div
      className={`${styles.quantityContainer} ${isCompact ? styles.compact : ""}`}
    >
      <button
        className={styles.quantityButton}
        onClick={quantity === 1 ? onRemove : onDecrease}
      >
        {quantity === 1 ? (
          <MdDelete size={20} color={theme.primary} />
        ) : (
          <MdRemove size={20} color={theme.primary} />
        )}
      </button>
      <div className={styles.quantityDisplay}>
        <span className={styles.quantityText}>{quantity}</span>
        <span className={styles.quantityUnit}>шт</span>
      </div>
      <button
        className={`${styles.quantityButton} ${isIncreaseDisabled ? styles.disabledButton : ""}`}
        onClick={onIncrease}
        disabled={isIncreaseDisabled}
      >
        <MdAdd
          size={20}
          color={
            isIncreaseDisabled ? "var(--text-secondary)" : "var(--primary)"
          }
        />
      </button>
    </div>
  );

  const renderActions = () => {
    if (quantity > 0) {
      return renderQuantityControls();
    }

    // Одна большая кнопка "В проект"
    if (showComplexButton && !showCartButton) {
      return (
        <button
          className={`${styles.complexButton} ${isCompact ? styles.compact : ""}`}
          onClick={onAddToComplex}
        >
          {isInComplexCart ? (
            <MdCheck size={20} color={isCompact ? theme.primary : "#fff"} />
          ) : (
            <MdPlaylistAdd size={20} color={isCompact ? theme.primary : "#fff"} />
          )}
          <span
            className={`${styles.addButtonText} ${isCompact ? styles.compact : ""}`}
          >
            {isInComplexCart ? "В проекте" : "В проект"}
          </span>
        </button>
      );
    }

    // Обычная категория: "В корзину" + маленькая "В проект"
    return (
      <div className={styles.buttonsRow}>
        {showCartButton && (
          <button
            className={`${styles.addButton} ${!hasPrice || isOutOfStock ? styles.addButtonDisabled : ""} ${isCompact ? styles.compact : ""}`}
            onClick={onAdd}
            disabled={!hasPrice || isOutOfStock}
          >
            <MdAddShoppingCart
              size={20}
              color={isCompact ? theme.primary : "#fff"}
            />
            <span
              className={`${styles.addButtonText} ${isCompact ? styles.compact : ""}`}
            >
              В корзину
            </span>
          </button>
        )}
        {showComplexButton && (
          <button
            className={`${styles.complexButtonSmall} ${isCompact ? styles.compact : ""}`}
            onClick={onAddToComplex}
          >
            {isInComplexCart ? (
              <MdCheck size={20} color="var(--primary)" />
            ) : (
              <MdPlaylistAdd size={20} color="var(--primary)" />
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <section
      className={`${styles.cartSection} ${styles[layout]} ${isCompact ? styles.compact : ""}`}
    >
      <div className={styles.cartRow}>
        <div className={styles.priceWrapper}>
          <span className={styles.cartPrice}>
            {hasPrice ? `${formatPriceToLocale(price)} ₽` : ""}
            {unitText && ` / ${unitText}`}
          </span>
          {maxAvailable !== undefined && maxAvailable < Infinity && (
            <span className={styles.maxAvailable}>{maxAvailable} шт</span>
          )}
        </div>
      </div>

      {renderActions()}
    </section>
  );
};